import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS, PERMISSION_DENIED_HINT } from '@/lib/permissions'
import type {
  ConfirmationChannel, CreateEventBody, DurationUnit, EmployeeDto, EmployeeSkillType, InstallationDto,
  ScheduleEventDto, ScheduleEventKind, SlotStartBody, WorkItemStatus,
} from '../../../../lib/api/v2/planning'
import * as planningApi from '../../../../lib/api/v2/planning'
import { surveyPoseGapWorkingDays } from '../../../../lib/working-days'
import { kindLabel, kindHasConfirmation, isConfirmationLive, settledLabel, kindHasFreeLabel, isFreeKind, slotStart, isPlaceableTime, spillsPastMidnight, sortEmployeeLanes, clampFicheTop, FICHE_MARGIN } from '../planning-helpers'
import { START_TIME_STEP_SECONDS, isOutsideWorkDay, workDayLabel } from '../planning-settings'
import { poseBudget, slotsOfPose, toHours, formatDuration, poseAcceptsNewSlot } from '../work-item-slots'
import { ConfirmSlotForm, channelLabel } from '../sidebar/ProjectDetailForms'
import { NoEligibleEmployees } from '../NoEligibleEmployees'
import { Textarea } from '../../../ui/textarea'
import { cn } from '../../../../lib/utils'

/** Brussels DD/MM/YYYY for the confirmation-warning banner. */
function formatConfirmedDay(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Brussels' })
}

/** Skill the kind requires (mirrors BE SchedulingConstraints.RequiredSkill). */
function requiredSkill(kind: ScheduleEventKind): EmployeeSkillType | null {
  if (kind === 'TechnicalSurvey') return 'Surveyor'
  if (kind === 'Installation') return 'Installer'
  return null
}

export interface SchedulingFicheProps {
  x: number
  y: number
  mode: 'create' | 'edit'
  kind: ScheduleEventKind
  projectId?: string | null
  technicalSurveyId?: string | null
  installationId?: string | null
  initialUnit: DurationUnit
  initialValue: number
  initialDayIso: string
  /** Wall clock the field opens on, « HH:mm » (an hour number until 2026-08-18 — no 18h30). */
  initialStartTime: string
  initialAssignees: string[]
  /** Note prefill (edit mode). Every kind carries one since SOLY-PLAN-004. */
  initialLabel?: string
  /**
   * Writes the slot's note. Its own call, never folded into the reschedule: on a pose or a métré
   * the reschedule endpoint ignores the label outright, and even where it does not, rescheduling is
   * what voids a client confirmation. Omitted → the note is only sent at creation.
   */
  onSetNote?: (id: string, note: string | null) => Promise<void>
  /** ISO instant this slot was confirmed, or null (« Proposé ») — drives the block and the warning. */
  confirmedAt?: string | null
  /** Channel + note of that confirmation, when there is one — read-only recap in the status block. */
  confirmationChannel?: ConfirmationChannel | null
  confirmationNote?: string | null
  /** Confirms the slot (Proposé → Confirmé). Omitted → the status block offers no gesture. */
  onConfirm?: (id: string, channel: ConfirmationChannel, note: string | undefined) => Promise<void>
  /** Puts it back to Proposé. Omitted → no inverse gesture. */
  onUnconfirm?: (id: string) => Promise<void>
  /** Lifecycle of the work item behind the slot — what ends the confirmation question (see
      SlotStatusBlock). Always null on a RDV, which has no work item and never settles. */
  workItemStatus?: WorkItemStatus | null
  /** When that work item was closed by a report — the date behind « Clôturé le … », label only. */
  workItemClosedAt?: string | null
  /** Reclassifies the slot between the two free kinds. Omitted → the gesture is not offered. */
  onConvertKind?: (id: string, kind: ScheduleEventKind) => Promise<void>
  employees: EmployeeDto[]
  /** Public-holiday dates (yyyy-MM-dd) — drives the non-working-day notice under the date field. */
  holidays?: Set<string>
  eventId?: string
  onClose: () => void
  onCreate: (body: CreateEventBody) => Promise<void>
  onReschedule?: (id: string, body: { durationUnit: DurationUnit; durationValue: number; assignedUserIds: string[]; reason?: string } & SlotStartBody) => Promise<void>
  onCancelEvent?: (id: string, reason: string) => Promise<void>
  /** SOLY-PLAN-003 MS-08 — opens a blank fiche for one MORE créneau on the same pose, same crew. */
  onAddSlot?: (args: { installationId: string; projectId: string; assignees: string[] }) => void
  /** Jumps the fiche to another créneau of the same pose (the list below the budget). Receives the
   *  whole slot, not its id: the list covers créneaux outside the loaded agenda window. */
  onOpenSlot?: (slot: ScheduleEventDto) => void
}

/**
 * Budget + créneaux of a pose — the surface where a work is composed out of calendar blocks
 * (SOLY-PLAN-003 MS-08, _design.md §1).
 *
 * Deliberately a RECAP plus a discreet gesture rather than N editable blocks stacked in the popover:
 * the fiche is used on a tablet, and stacking full forms is what pushes the action buttons below the
 * fold (the trap already met at the ship of SOLY-PO-001). One créneau is edited at a time — the
 * nominal case, one pose one créneau, pays no complexity at all.
 */
function PoseSlotsBlock({ pose, projectId, slots, currentEventId, draftHours, crew, onAddSlot, onOpenSlot }: {
  pose: InstallationDto | null
  projectId: string
  slots: ScheduleEventDto[]
  currentEventId?: string
  draftHours: number
  /** Crew of the créneau being edited — the new block starts with the same one (décision 5). */
  crew: string[]
  onAddSlot?: (args: { installationId: string; projectId: string; assignees: string[] }) => void
  onOpenSlot?: (slot: ScheduleEventDto) => void
}) {
  if (!pose) return null

  const budget = poseBudget(pose, slots)
  const unit = pose.estimatedDurationUnit
  // The slot being edited counts with the duration currently TYPED, not the saved one: the recap
  // must answer « what happens if I save this », otherwise the user reads a stale budget while
  // deciding whether to add another créneau.
  const savedCurrent = currentEventId ? slots.find((s) => s.id === currentEventId) : undefined
  const planned = budget.plannedHours - (savedCurrent ? toHours(savedCurrent.durationUnit, savedCurrent.durationValue) : 0) + draftHours
  // No budget → no arithmetic against it (SOLY-PLAN-004). Computing « il reste » or « au-delà de
  // l'estimation » from a missing estimate would announce a gap nobody defined.
  const estimated = budget.estimatedHours
  const remaining = estimated === null ? 0 : Math.max(0, estimated - planned)
  const over = estimated === null ? 0 : Math.max(0, planned - estimated)

  return (
    <div className="mb-2 rounded border bg-muted/20 px-2 py-1.5 text-xs" data-testid="planning-fiche-budget">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase text-muted-foreground">Budget</span>
        <span className="font-medium">
          planifié {formatDuration(planned, unit)}
          {estimated === null
            ? <span className="ml-1 font-normal text-muted-foreground">· pose non estimée</span>
            : <> / estimé {formatDuration(estimated, unit)}</>}
        </span>
      </div>
      {remaining > 0 && (
        <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid="planning-fiche-budget-remaining">
          Il reste {formatDuration(remaining, unit)} à planifier
        </div>
      )}
      {/* Décision 7: an overrun is INFORMATION. Never red, never a warning icon — « c'était un peu
          short » is the nominal case, not a fault. */}
      {over > 0 && (
        <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid="planning-fiche-budget-over">
          +{formatDuration(over, unit)} au-delà de l'estimation
        </div>
      )}

      {slots.length > 1 && (
        <ul className="mt-1.5 space-y-0.5 border-t pt-1.5">
          {slots.map((s, i) => (
            <li key={s.id} data-testid={`planning-fiche-slot-${i}`}
              className={cn('flex items-center justify-between gap-2', s.id === currentEventId && 'font-medium')}>
              <button type="button" className="truncate text-left hover:underline disabled:no-underline"
                disabled={s.id === currentEventId || !onOpenSlot}
                onClick={() => onOpenSlot?.(s)}>
                {i + 1}/{slots.length} · {formatSlotDay(s.startDate)} · {s.durationUnit === 'Days' ? `${s.durationValue} j` : `${s.durationValue} h`}
              </button>
              {s.id === currentEventId && <span className="shrink-0 text-[10px] uppercase text-muted-foreground">ouvert</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Discreet, never a primary button: one créneau is the default, adding one is a gesture
          (décision 6). Hidden once the report closes the pose — the UI mirrors the domain refusal
          of MS-07 rather than offering a gesture the backend would reject. */}
      {onAddSlot && poseAcceptsNewSlot(pose) && (
        <button type="button" data-testid="planning-fiche-add-slot"
          className="mt-1.5 text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => onAddSlot({ installationId: pose.id, projectId, assignees: crew })}>
          + Ajouter un créneau
        </button>
      )}
      {!poseAcceptsNewSlot(pose) && (
        <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid="planning-fiche-slot-closed">
          Rapport saisi — la pose est clôturée. Créez une finition depuis le chantier.
        </div>
      )}
    </div>
  )
}

/** « lun. 15/06 » — enough to recognise a créneau in a list without re-reading the year. */
function formatSlotDay(date: string): string {
  const d = new Date(`${date}T12:00:00Z`)
  return d.toLocaleDateString('fr-BE', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' })
}


/**
 * Start time of a slot — « HH:mm », half-hour steps (SOLY-PLAN-004).
 *
 * A <select> of whole hours built from the org work day used to stand here, which is why « un RDV à
 * 18h30 » could not be entered at all: neither the minutes nor the evening existed in the list. The
 * work day is now what PREFILLS the field and what the hint below refers to — it stopped being the
 * set of what can be picked. The backend never imposed working hours (ScheduleEvent.ValidateDuration
 * says so explicitly), so the frontend had to stop imposing them alone.
 *
 * The hint is deliberately grey, not amber: planning outside working hours is a decision, not a
 * mistake — Élodie takes evening appointments on purpose.
 *
 * One component for the three places the fiche asks the question (RDV/Interne, pose, métré): the
 * same control must not drift into three.
 */
function StartTimeField({ label, value, onChange, className }: {
  label: string
  value: string
  onChange: (value: string) => void
  className: string
}) {
  return (
    <label className={className}>
      <span className="text-[11px] uppercase text-muted-foreground">{label}</span>
      <input
        type="time" step={START_TIME_STEP_SECONDS} value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="planning-fiche-start"
        className="mt-0.5 w-full rounded border px-2 py-1" />
      {isOutsideWorkDay(value) && (
        <span className="mt-1 block text-[11px] text-muted-foreground" data-testid="planning-fiche-outside-workday">
          ℹ Hors des heures de travail ({workDayLabel()})
        </span>
      )}
    </label>
  )
}

/**
 * Notice under the date field when the picked day is a weekend/holiday (day policy round 6):
 * neutral info for a RDV (allowed — the user just sees the conscious-choice flag), amber warning
 * for métré/pose (the BE will refuse). Purely informative — the BE stays the enforcer.
 */
function NonWorkingDayNotice({ dayIso, kind, holidays }: { dayIso: string; kind: ScheduleEventKind; holidays?: Set<string> }) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso)) return null
  const dow = new Date(`${dayIso}T12:00:00Z`).getUTCDay()
  const isHoliday = holidays?.has(dayIso) ?? false
  if (dow !== 0 && dow !== 6 && !isHoliday) return null
  const dayName = isHoliday ? 'Jour férié' : dow === 6 ? 'Samedi' : 'Dimanche'
  // Both free kinds may be planned any day (BE SchedulingConstraints.DayPolicy) — an Interne
  // inherits the RDV's freedom, otherwise reclassifying a Saturday montage would refuse it.
  if (isFreeKind(kind)) {
    return (
      <span className="mt-1 block text-[11px] text-muted-foreground" data-testid="planning-fiche-nonworking-info">
        ℹ {dayName} — jour non ouvré
      </span>
    )
  }
  return (
    <span className="mt-1 block text-[11px] text-amber-600" data-testid="planning-fiche-nonworking-warn">
      ⚠ {dayName} — les {kind === 'Installation' ? 'poses' : 'métrés'} sont planifiables en jours ouvrés uniquement
    </span>
  )
}

/** The pose anchor + threshold needed for the M2 « métré trop proche de la pose » fiche warning. */
interface PoseGapInputs { poseIso: string; minDays: number }

/**
 * M2 warning at scheduling time (_constraints.md « cohérence amont ») : when the picked métré date
 * is fewer working days from the planned pose than the chantier's effective minimum, say so HERE —
 * the user must never be surprised by a Suivi alert we could have shown at the moment of choice.
 * Same gap convention and numbers as the BE engine: the gap counts from the métré's LAST day
 * (a Days métré spanning N days ends on dayIso + N − 1), mirroring AlertEngine.GapWorkingDays.
 * A métré landing on or after the pose day gets its own wording (a « 0 j ouvrables » readout is
 * exact but cryptic there). Non-blocking.
 */
function SurveyPoseGapNotice({ dayIso, unit, value, inputs }: {
  dayIso: string
  unit: DurationUnit
  value: number
  inputs: PoseGapInputs | null
}) {
  if (!inputs || !/^\d{4}-\d{2}-\d{2}$/.test(dayIso)) return null
  const spanDays = unit === 'Days' ? Math.max(1, Math.ceil(value)) : 1
  const lastDayIso = new Date(new Date(`${dayIso}T00:00:00Z`).getTime() + (spanDays - 1) * 86_400_000)
    .toISOString().slice(0, 10)
  const [y, m, d] = inputs.poseIso.split('-')
  if (lastDayIso >= inputs.poseIso) {
    return (
      <span className="mt-1 block text-[11px] text-amber-600" data-testid="planning-fiche-pose-gap-warn">
        ⚠ Ce métré tombe le jour de la pose du {d}/{m}/{y} ou après
      </span>
    )
  }
  const gap = surveyPoseGapWorkingDays(lastDayIso, inputs.poseIso)
  if (gap >= inputs.minDays) return null
  return (
    <span className="mt-1 block text-[11px] text-amber-600" data-testid="planning-fiche-pose-gap-warn">
      ⚠ Ce métré est à {gap} j ouvrables de la pose du {d}/{m}/{y} — minimum {inputs.minDays}
    </span>
  )
}

/**
 * « Statut du créneau » — where a RDV finally becomes confirmable (SOLY-PLAN-002 MS-04).
 *
 * It lives HERE and not in the chantier drawer because a RDV has no project, so it has no drawer:
 * the agenda fiche is the only surface it can be reached from. The channel + note form is the one
 * the drawer already uses — the same gesture must not grow two implementations that drift.
 *
 * Not rendered for internal work (nobody to agree), nor in create mode (nothing to confirm yet).
 * No optimistic update: the agenda is a source of truth, we do not paint a state the server has not
 * acknowledged. The refreshed slot comes back from the parent through props.
 */
function SlotStatusBlock({ eventId, kind, confirmedAt, channel, note, workItemStatus, closedAt, onConfirm: onConfirmProp, onUnconfirm: onUnconfirmProp }: {
  eventId: string
  kind: ScheduleEventKind
  confirmedAt: string | null
  channel: ConfirmationChannel | null
  note: string | null
  workItemStatus: WorkItemStatus | null
  closedAt: string | null
  onConfirm?: (id: string, channel: ConfirmationChannel, note: string | undefined) => Promise<void>
  onUnconfirm?: (id: string) => Promise<void>
}) {
  const [form, setForm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { can } = usePermissions()
  // SOLY-PERM-001 — the recap stays (« confirmé le 14, par téléphone » is information); the gesture
  // goes. Folding it into the existing « no callback → no gesture » rule keeps one rule, not two.
  const canManage = can(PERMISSIONS.PLANNING_MANAGE)
  const onConfirm = canManage ? onConfirmProp : undefined
  const onUnconfirm = canManage ? onUnconfirmProp : undefined

  if (!kindHasConfirmation(kind)) return null

  // The gesture ends when the WORK ITEM settles, not when the date passes — reversing what this
  // block used to do. Blocking on the end date was the bug: a RDV the client never confirmed is a
  // RDV that did not happen, and the secretary was left with a slot she could neither confirm nor
  // was allowed to stop being asked about. Now she can still confirm it late, or move it. A RDV
  // has no work item at all, so its gesture never expires.
  //
  // ⚠️ This used to claim the drawer reads the same predicate. It does not: WorkItemSlotCard carries no
  // lifecycle check and relies on its caller never rendering a settled work item's slots at all.
  // Same outcome, different mechanism — stated plainly rather than asserted away.
  const settled = settledLabel(kind, workItemStatus, closedAt)
  const isSettled = !isConfirmationLive({ kind, workItemStatus })

  const run = async (action: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await action()
      setForm(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action refusée')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="mb-2 rounded border bg-muted/20 px-2 py-1.5" aria-label="Statut du créneau" data-testid="planning-fiche-status">
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="uppercase text-muted-foreground">Statut du créneau</span>
        {/* Settled wins over confirmed. A pose that WAS confirmed and is now closed used to read
            « ✓ Confirmé le … » here while its own agenda bar said « clôturé le … » — the two
            surfaces of one slot contradicting each other, which is the exact defect this whole
            change exists to remove. Found by adversarial review; the tests only ever covered a
            terminal slot that had NEVER been confirmed, so the ordering was never exercised. */}
        {settled ? (
          <span className="font-medium first-letter:uppercase text-muted-foreground" data-testid="planning-fiche-status-state">
            {settled}
          </span>
        ) : confirmedAt ? (
          <span className="font-medium text-emerald-700" data-testid="planning-fiche-status-state">
            ✓ Confirmé le {formatConfirmedDay(confirmedAt)}
          </span>
        ) : (
          <span className="font-medium text-amber-700" data-testid="planning-fiche-status-state">Proposé</span>
        )}
      </div>

      {/* The recap follows the state line it belongs to. On a settled slot the status now reads
          « clôturé le … », so a bare « Téléphone · « ok par tél » » underneath had nothing left to
          attach to — it names the confirmation instead, which is history worth keeping. */}
      {confirmedAt && (channel || note || settled) && (
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {settled && <span>confirmé le {formatConfirmedDay(confirmedAt)}{(channel || note) && ' · '}</span>}
          {channel && <span>{channelLabel(channel)}</span>}
          {channel && note && <span> · </span>}
          {note && <span>« {note} »</span>}
        </div>
      )}

      {error && <p className="mt-1 text-[11px] text-red-600" data-testid="planning-fiche-status-error">{error}</p>}

      {!form && !isSettled && (onConfirm || onUnconfirm) && (
        <div className="mt-1 flex justify-end">
          {confirmedAt
            ? onUnconfirm && (
              <button type="button" disabled={busy} data-testid="planning-event-unconfirm-btn"
                onClick={() => void run(() => onUnconfirm(eventId))}
                className="text-[11px] font-medium text-muted-foreground hover:underline disabled:opacity-50">
                Remettre en proposé
              </button>
            )
            : onConfirm && (
              <button type="button" disabled={busy} data-testid="planning-event-confirm-btn"
                onClick={() => setForm(true)}
                className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50">
                Confirmer
              </button>
            )}
        </div>
      )}

      {form && !isSettled && onConfirm && (
        <ConfirmSlotForm
          area="event"
          onSubmit={(c, n) => run(() => onConfirm(eventId, c, n))}
          onCancel={() => setForm(false)}
        />
      )}
    </section>
  )
}

/**
 * « Ce n'est pas un rendez-vous → Interne », and back (SOLY-PLAN-002 MS-05).
 *
 * This gesture is what makes « on ne touche pas aux données existantes » liveable: the montages
 * atelier already encoded as RDV get reclassified one by one, keeping their date and their crew,
 * instead of being deleted and re-created.
 *
 * Converting to Interne ERASES the confirmation server-side — correctly, since internal work has no
 * counterparty. The domain does it without flinching, so it is the UI's job to say so BEFORE. The
 * dialog NAMES what is lost; it does not ask « êtes-vous sûr ? », which tells the user nothing.
 */
function ReclassifyGesture({ eventId, kind, confirmedAt, onConvert: onConvertProp }: {
  eventId: string
  kind: ScheduleEventKind
  confirmedAt: string | null
  onConvert: (id: string, target: ScheduleEventKind) => Promise<void>
}) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { can } = usePermissions()
  const onConvert = can(PERMISSIONS.PLANNING_MANAGE) ? onConvertProp : null

  const target: ScheduleEventKind = kind === 'Appointment' ? 'InternalWork' : 'Appointment'
  const losesConfirmation = target === 'InternalWork' && confirmedAt != null

  const convert = async () => {
    if (busy || !onConvert) return
    setBusy(true)
    setError(null)
    try {
      await onConvert(eventId, target)
      setConfirming(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reclassement refusé')
    } finally {
      setBusy(false)
    }
  }

  // No permission → the whole gesture disappears; there is nothing to read in it.
  if (!onConvert) return null

  return (
    <div className="mb-2">
      <button
        type="button"
        disabled={busy}
        data-testid="planning-fiche-reclassify-btn"
        onClick={() => (losesConfirmation ? setConfirming(true) : void convert())}
        className="text-[11px] text-muted-foreground hover:underline disabled:opacity-50"
      >
        {target === 'InternalWork' ? "Ce n'est pas un rendez-vous → Interne" : 'C’est un rendez-vous → RDV'}
      </button>

      {error && <p className="mt-1 text-[11px] text-red-600" data-testid="planning-fiche-reclassify-error">{error}</p>}

      {confirming && (
        <div
          role="alertdialog"
          aria-label="Reclasser en Interne"
          data-testid="planning-fiche-reclassify-dialog"
          className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
        >
          <p>
            Un créneau Interne n’a pas de contrepartie : la confirmation du{' '}
            {formatConfirmedDay(confirmedAt ?? '')} sera effacée, et ce créneau n’aura plus de statut
            Proposé / Confirmé.
          </p>
          <div className="mt-1.5 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirming(false)} data-testid="planning-fiche-reclassify-cancel"
              className="text-[11px] text-muted-foreground hover:underline">
              Garder le RDV
            </button>
            <button type="button" disabled={busy} onClick={() => void convert()} data-testid="planning-fiche-reclassify-confirm"
              className="rounded bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white disabled:opacity-50">
              Reclasser en Interne
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** One-tap reschedule reason chips (edit mode) → fills the reason input so l'historique shows WHY.
    `id` is the ASCII testid slug; `label` is the FR text written into the reason. */
const REASON_CHIPS = [
  { id: 'client', label: 'Client' },
  { id: 'fournisseur', label: 'Fournisseur' },
  { id: 'meteo', label: 'Météo' },
  { id: 'interne', label: 'Interne' },
] as const

/**
 * Unified scheduling fiche (SOLY-PLAN-001 MS-18) — replaces drag&drop + the quick-add popover.
 * Create or edit an event: employee(s), date, unit (Heures/Jours), start+duration / nb days.
 * Server conflicts (overlap/congé/skill — MS-17) are surfaced inline; submit stays blocked until fixed.
 * Esc / click-out closes.
 */
export function SchedulingFiche(props: SchedulingFicheProps) {
  // SOLY-PERM-001 — the fiche stays READABLE without `planning:manage` (it is how one checks who is
  // on a slot and whether the client agreed); only the gestures go. Gated here rather than by
  // withholding the parent's callbacks, because the submit button does not hang off one of them.
  const { can } = usePermissions()
  const canManage = can(PERMISSIONS.PLANNING_MANAGE)
  const { x, y, mode, employees, onClose } = props
  // The kind is LOCAL state, not a plain prop: in create mode the user picks between the two free
  // kinds before submitting, and in edit mode a reclassement changes it in place (the parent also
  // refetches the agenda, but the open fiche must not keep showing the old kind).
  const [kind, setKind] = useState<ScheduleEventKind>(props.kind)
  const [unit, setUnit] = useState<DurationUnit>(props.initialUnit)
  const [value, setValue] = useState<number>(props.initialValue)
  const [dayIso, setDayIso] = useState<string>(props.initialDayIso)
  const [startTime, setStartTime] = useState<string>(props.initialStartTime)
  const [assignees, setAssignees] = useState<Set<string>>(new Set(props.initialAssignees))
  const [label, setLabel] = useState(props.initialLabel ?? '')
  const [showCancel, setShowCancel] = useState(false)
  const [reason, setReason] = useState('')
  const [rescheduleReason, setRescheduleReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [poseGapInputs, setPoseGapInputs] = useState<PoseGapInputs | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  // Where the fiche actually sits, once we know how tall it turned out to be (see below).
  const [top, setTop] = useState<number>(() => Math.max(FICHE_MARGIN, y))
  // Kept in state so the max-height follows a rotation, like the position does.
  const [viewportHeight, setViewportHeight] = useState<number>(() => window.innerHeight)

  // M2 inputs (métré only): the chantier's planned pose date + its effective minimum gap. Fetched
  // once per fiche; failures stay silent — the warning is informative, the BE remains the judge.
  const projectId = props.projectId
  useEffect(() => {
    if (kind !== 'TechnicalSurvey' || !projectId) return
    let active = true
    void (async () => {
      try {
        const [detail, constraints] = await Promise.all([
          planningApi.getProjectDetail(projectId),
          planningApi.getProjectConstraints(projectId),
        ])
        const rule = constraints.rules.find((r) => r.key === 'SurveyLeadMaxDaysBeforePose')
        if (!rule || rule.dismissed) return
        // Same anchor as the BE: the soonest Open pose with a live RDV from today on.
        const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Brussels' })
        const poseIso = detail.installations
          .filter((i) => i.status === 'Open' && i.currentEventId)
          .map((i) => detail.events.find((e) => e.id === i.currentEventId)?.startDate)
          .filter((d): d is string => !!d && d >= todayIso)
          .sort()[0]
        if (active && poseIso) setPoseGapInputs({ poseIso, minDays: rule.days })
      } catch {
        // ignore — never block scheduling on a missing hint
      }
    })()
    return () => { active = false }
  }, [kind, projectId])

  // SOLY-PLAN-003 MS-08 — the pose's budget and its other créneaux. Same source as the M2 hint
  // above (the project detail carries every event of the project, not just the agenda window), so
  // a créneau three weeks out still counts. Failures stay silent: the recap is a help, and losing
  // it must never block the scheduling gesture itself.
  const installationId = props.installationId
  const [pose, setPose] = useState<InstallationDto | null>(null)
  const [poseSlots, setPoseSlots] = useState<ScheduleEventDto[]>([])
  useEffect(() => {
    if (kind !== 'Installation' || !projectId || !installationId) return
    let active = true
    void (async () => {
      try {
        const detail = await planningApi.getProjectDetail(projectId)
        if (!active) return
        setPose(detail.installations.find((i) => i.id === installationId) ?? null)
        setPoseSlots(slotsOfPose(detail.events, installationId))
      } catch {
        // ignore — never block scheduling on a missing recap
      }
    })()
    return () => { active = false }
  }, [kind, projectId, installationId])

  /**
   * Keep the fiche's BOTTOM on screen — by OBSERVING the element, never by listing what might have
   * resized it.
   *
   * The submit has fallen below the fold three times now. Twice from a hardcoded height reserve
   * (360 px, then 470 px). The third time from the obvious-looking fix: re-measure on a
   * hand-written dependency array. That array cannot work, and not because it was incomplete —
   * because it is the wrong instrument:
   *
   *  · it missed `dayIso`, and the non-working-day notice is on the MOST travelled path
   *    (« + Ajouter un créneau » opens with a blank date on purpose, so a date is always typed);
   *  · `poseGapInputs` lands after two awaited fetches, i.e. after the measurement;
   *  · and above all, the blocks that grow the fiche most carry their own state INSIDE child
   *    components — the confirmation form of SlotStatusBlock, the reclassement dialog of
   *    ReclassifyGesture. Those re-render the child, not this component. No dependency array of
   *    this component can ever see them.
   *
   * A ResizeObserver watches the rendered box, so it answers the only question that matters —
   * « did this get taller? » — whatever made it happen. Plus a `resize` listener, because
   * `window.innerHeight` is the other half of the computation and this fiche is driven on a tablet
   * (rotation, virtual keyboard).
   *
   * No loop: the height does not depend on `top` (the max-height is relative to the viewport), so
   * one pass converges.
   *
   * useLayoutEffect, not useEffect: applied before the browser paints, so the fiche never flashes
   * at the wrong place.
   */
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const apply = () => {
      setViewportHeight(window.innerHeight)
      setTop(clampFicheTop(y, el.offsetHeight, window.innerHeight))
    }
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(el)
    window.addEventListener('resize', apply)
    return () => { observer.disconnect(); window.removeEventListener('resize', apply) }
  }, [y])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [onClose])

  const skill = requiredSkill(kind)
  // Same order as the agenda lanes: the chips and the grid show one roster, so they must not
  // disagree. The function grouping is a no-op once `skill` has filtered (a pose only lists
  // Placeurs) — what this really buys is the alphabetical order the raw API list did not have.
  const eligible = useMemo(
    () => sortEmployeeLanes(skill ? employees.filter((e) => e.skills.includes(skill)) : employees),
    [employees, skill],
  )

  const toggle = (userId: string) =>
    setAssignees((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId); else next.add(userId)
      return next
    })

  const submit = async () => {
    if (submitting) return
    if (value <= 0) { setError('La durée doit être supérieure à zéro.'); return }
    // A blank date is now reachable: « + Ajouter un créneau » opens the fiche with no day on
    // purpose — the second date is precisely what is not known yet (scénario S4). Without this
    // guard the empty input would be turned into a nonsense instant and sent to the backend.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso)) { setError('Choisissez une date pour ce créneau.'); return }
    // An <input type="time"> can be left blank, and 00:00 is the Days anchor the backend refuses on
    // an hourly slot. Both would reach the API as a nonsense instant.
    if (unit === 'Hours' && !isPlaceableTime(startTime)) { setError('Choisissez une heure de début.'); return }
    if (unit === 'Hours' && spillsPastMidnight(startTime, value)) {
      setError('Un créneau en heures doit se terminer le même jour.'); return
    }
    setSubmitting(true)
    setError(null)
    const start = slotStart(dayIso, unit, startTime)
    const ids = [...assignees]
    try {
      if (mode === 'edit' && props.eventId && props.onReschedule) {
        await props.onReschedule(props.eventId, {
          durationUnit: unit, durationValue: value, ...start, assignedUserIds: ids,
          reason: rescheduleReason.trim() || undefined,
        })
        // Only when it actually changed: the note endpoint is cheap, but an unchanged write would
        // add a history line saying something happened when nothing did.
        const note = label.trim()
        if (props.onSetNote && note !== (props.initialLabel ?? '').trim()) {
          await props.onSetNote(props.eventId, note || null)
        }
      } else {
        const body: CreateEventBody = {
          kind,
          projectId: props.projectId ?? null,
          technicalSurveyId: props.technicalSurveyId ?? null,
          installationId: props.installationId ?? null,
          durationUnit: unit,
          durationValue: value,
          ...start,
          assignedUserIds: ids,
          label: label.trim() || undefined,
        }
        await props.onCreate(body)
      }
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action refusée')
    } finally {
      setSubmitting(false)
    }
  }

  const cancelEvent = async () => {
    if (submitting || !props.eventId || !props.onCancelEvent) return
    if (reason.trim().length < 5) { setError('Motif d’annulation requis (≥ 5 caractères).'); return }
    setSubmitting(true)
    setError(null)
    try {
      await props.onCancelEvent(props.eventId, reason.trim())
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Annulation refusée')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      ref={ref}
      data-testid="planning-fiche"
      className="fixed z-50 w-72 overflow-y-auto rounded-lg border bg-background p-3 shadow-lg"
      style={{
        // Measured, not reserved — see the useLayoutEffect above.
        top,
        left: Math.min(x, window.innerWidth - 300),
        // The cap comes from the SAME source as the measurement. It used to be a Tailwind
        // `max-h-[calc(100vh-16px)]`, and on mobile `100vh` (large viewport, toolbars included) is
        // TALLER than window.innerHeight: a fiche landing between the two was capped by neither —
        // no internal scroll, and a `fixed` element the page cannot scroll to. Two ceilings from
        // two sources is how they drift apart.
        maxHeight: viewportHeight - 2 * FICHE_MARGIN,
      }}
    >
      <div className="mb-2 text-xs font-semibold text-muted-foreground">
        {mode === 'edit' ? 'Modifier' : 'Planifier'} · {kindLabel(kind)}
      </div>

      {/* Kind picker — only between the two FREE kinds, and only at creation. A métré/pose is born
          from its work item and never offered here (the corridor is closed in the domain; the UI
          simply does not propose what the domain would refuse). */}
      {mode === 'create' && isFreeKind(kind) && (
        <div className="mb-2 inline-flex overflow-hidden rounded border text-xs" role="group" aria-label="Type de créneau">
          {(['Appointment', 'InternalWork'] as const).map((k) => (
            <button key={k} type="button" data-testid={`planning-fiche-kind-${k}`}
              aria-pressed={kind === k} onClick={() => setKind(k)}
              className={cn('border-l px-3 py-1 first:border-l-0', kind === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
              {kindLabel(k)}
            </button>
          ))}
        </div>
      )}

      {/* Reclassement — a discreet gesture, never a primary button: it is a correction, not a step
          of the flow. Offered only on the two free kinds (MS-03's corridor). */}
      {mode === 'edit' && props.eventId && props.onConvertKind && isFreeKind(kind) && (
        <ReclassifyGesture
          eventId={props.eventId}
          kind={kind}
          confirmedAt={props.confirmedAt ?? null}
          onConvert={async (id, target) => {
            await props.onConvertKind!(id, target)
            setKind(target)
          }}
        />
      )}

      {/* Statut du créneau — état courant + geste, et son inverse (SOLY-PLAN-002 MS-04). */}
      {mode === 'edit' && props.eventId && (
        <SlotStatusBlock
          eventId={props.eventId}
          kind={kind}
          confirmedAt={props.confirmedAt ?? null}
          channel={props.confirmationChannel ?? null}
          note={props.confirmationNote ?? null}
          workItemStatus={props.workItemStatus ?? null}
          closedAt={props.workItemClosedAt ?? null}
          onConfirm={props.onConfirm}
          onUnconfirm={props.onUnconfirm}
        />
      )}

      {/* Garde-fou (Elodie 2026-06-14) : déplacer un créneau confirmé le remet en proposé (invariant
          domaine ScheduleEvent.Reschedule). On le dit AVANT le geste, pas après.
          SOLY-PLAN-002 : le cas spécial `kind !== 'Appointment'` est retiré — il cachait à l'écran
          un comportement que le BE appliquait déjà aux RDV. */}
      {/* … and NOT once the work item is CLOSED or CANCELLED: the warning describes what a
          reschedule would cost, and on a finished or dead job there is no confirmation left to
          lose. It stayed up next to a « Clôturé le … » status until adversarial review pointed
          at it. NotApplicable is deliberately NOT in that list — marking a work item non
          nécessaire does not cancel its créneaux, so they stay reschedulable and the domain still
          clears the confirmation when they move. */}
      {mode === 'edit' && props.confirmedAt
        && props.workItemStatus !== 'Completed' && props.workItemStatus !== 'Cancelled' && (
        <div
          className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700"
          data-testid="planning-fiche-confirmed-warning"
        >
          ⚠ Confirmé le {formatConfirmedDay(props.confirmedAt)} — le replanifier le remettra en proposé.
        </div>
      )}

      {/* Note du créneau. Sur un RDV / de l'interne elle EST le titre de la barre (« Rencontrer
          M. Dupont ») ; sur une pose ou un métré c'est ce qu'on fait dans CE bloc, le chantier
          gardant le titre (Élodie 2026-08-18). Un <textarea> et non un <input> : « note sur
          plusieurs lignes » est sa demande, et une adresse ou un code d'accès ne tient pas sur une
          ligne. Le créneau porte 200 caractères (colonne V095). */}
      <label className="mb-2 block">
        <span className="text-[11px] uppercase text-muted-foreground">
          {kindHasFreeLabel(kind) ? 'Note' : 'Note du créneau'}
        </span>
        <Textarea
          value={label} maxLength={200} rows={2}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={kindHasFreeLabel(kind) ? 'Ex : Rencontrer M. Dupont' : 'Ex : on pose les plans de travail'}
          data-testid="planning-fiche-label"
          className="mt-0.5 min-h-0 text-xs"
        />
      </label>

      {/* Employees */}
      <div className="mb-2">
        <div className="mb-1 text-[11px] uppercase text-muted-foreground">Employé(s)</div>
        <div className="flex max-h-24 flex-wrap gap-1 overflow-auto" data-testid="planning-fiche-employees">
          {eligible.length === 0 && <NoEligibleEmployees skill={skill} testId="planning-fiche-no-eligible" />}
          {eligible.map((e) => {
            const on = assignees.has(e.userId)
            return (
              <button
                key={e.userId}
                type="button"
                data-testid={`planning-fiche-emp-${e.userId}`}
                aria-pressed={on}
                onClick={() => toggle(e.userId)}
                className={cn('rounded-full px-2 py-1 text-xs', on ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
              >
                {e.displayName || e.email}
              </button>
            )
          })}
        </div>
      </div>

      {/* Date */}
      <label className="mb-2 block">
        <span className="text-[11px] uppercase text-muted-foreground">Date</span>
        <input type="date" value={dayIso} onChange={(e) => setDayIso(e.target.value)} data-testid="planning-fiche-date"
          className="mt-0.5 w-full rounded border px-2 py-1 text-xs" />
        <NonWorkingDayNotice dayIso={dayIso} kind={kind} holidays={props.holidays} />
        <SurveyPoseGapNotice dayIso={dayIso} unit={unit} value={value} inputs={poseGapInputs} />
      </label>

      {/* Durée. RDV libre = éditable (pas de work item porteur). Métré/Pose = lecture seule : la durée
          vient de l'estimation du work item (durée unique, SOLY-PLAN-001), modifiable dans le cockpit. */}
      {isFreeKind(kind) ? (
        <>
          <div className="mb-2 inline-flex overflow-hidden rounded border text-xs" role="group" aria-label="Unité">
            {(['Hours', 'Days'] as const).map((u) => (
              <button key={u} type="button" data-testid={`planning-fiche-unit-${u}`} onClick={() => setUnit(u)}
                className={cn('border-l px-3 py-1 first:border-l-0', unit === u ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                {u === 'Hours' ? '⏱ Heures' : '📅 Jours'}
              </button>
            ))}
          </div>
          {unit === 'Hours' ? (
            <div className="mb-2 flex items-center gap-2 text-xs">
              <StartTimeField label="Début" value={startTime} onChange={setStartTime} className="flex-1" />
              <label className="flex-1">
                <span className="text-[11px] uppercase text-muted-foreground">Durée (h)</span>
                <input type="number" min={0.5} step={0.5} value={value} onChange={(e) => setValue(Number(e.target.value))} data-testid="planning-fiche-hours"
                  className="mt-0.5 w-full rounded border px-2 py-1" />
              </label>
            </div>
          ) : (
            <label className="mb-2 block text-xs">
              <span className="text-[11px] uppercase text-muted-foreground">Nombre de jours</span>
              <input type="number" min={1} step={1} value={value} onChange={(e) => setValue(Math.max(1, Math.round(Number(e.target.value))))} data-testid="planning-fiche-days"
                className="mt-0.5 w-full rounded border px-2 py-1" />
            </label>
          )}
        </>
      ) : kind === 'Installation' ? (
        <>
          {/* SOLY-PLAN-003 MS-08 — a pose créneau carries its OWN duration, and it is editable here.
              It used to be read-only (« durée unique »: the estimate WAS the placed duration, and
              changing it in the cockpit resized this slot). MS-05 made the estimate a budget, so
              that propagation is gone — without this field there would be no way left to say « ce
              créneau-ci fait deux jours ». Same grammar as the backend: whole days, or hours. */}
          <div className="mb-2 inline-flex overflow-hidden rounded border text-xs" role="group" aria-label="Unité du créneau">
            {(['Hours', 'Days'] as const).map((u) => (
              <button key={u} type="button" data-testid={`planning-fiche-unit-${u}`} onClick={() => setUnit(u)}
                className={cn('border-l px-3 py-1 first:border-l-0', unit === u ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                {u === 'Hours' ? '⏱ Heures' : '📅 Jours'}
              </button>
            ))}
          </div>
          {unit === 'Hours' ? (
            <div className="mb-2 flex items-center gap-2 text-xs">
              <StartTimeField label="Début" value={startTime} onChange={setStartTime} className="flex-1" />
              <label className="flex-1">
                <span className="text-[11px] uppercase text-muted-foreground">Durée (h)</span>
                <input type="number" min={0.5} step={0.5} value={value} onChange={(e) => setValue(Number(e.target.value))} data-testid="planning-fiche-hours"
                  className="mt-0.5 w-full rounded border px-2 py-1" />
              </label>
            </div>
          ) : (
            <label className="mb-2 block text-xs">
              <span className="text-[11px] uppercase text-muted-foreground">Nombre de jours</span>
              <input type="number" min={1} step={1} value={value} onChange={(e) => setValue(Math.max(1, Math.round(Number(e.target.value))))} data-testid="planning-fiche-days"
                className="mt-0.5 w-full rounded border px-2 py-1" />
            </label>
          )}

          <PoseSlotsBlock
            pose={pose}
            projectId={props.projectId ?? ''}
            slots={poseSlots}
            currentEventId={props.eventId}
            draftHours={toHours(unit, value)}
            crew={[...assignees]}
            onAddSlot={props.onAddSlot}
            onOpenSlot={props.onOpenSlot}
          />
        </>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between rounded border bg-muted/30 px-2 py-1 text-xs" data-testid="planning-fiche-duration">
            <span className="text-[11px] uppercase text-muted-foreground">Durée</span>
            <span className="font-medium">{unit === 'Days' ? `${value} j` : `${value} h`}<span className="ml-1 font-normal text-muted-foreground">· estimation</span></span>
          </div>
          {unit === 'Hours' && (
            <StartTimeField label="Heure de début" value={startTime} onChange={setStartTime} className="mb-2 block text-xs" />
          )}
        </>
      )}

      {/* Reschedule reason (edit only) — optional. Chips set it; free-text overrides. Surfaces WHY a
          plan moved in l'historique ("le client a appelé"). */}
      {mode === 'edit' && (
        <div className="mb-2">
          <div className="mb-1 text-[11px] uppercase text-muted-foreground">Raison (optionnelle)</div>
          <div className="mb-1 flex flex-wrap gap-1">
            {REASON_CHIPS.map((c) => (
              <button key={c.id} type="button" aria-pressed={rescheduleReason === c.label}
                data-testid={`planning-fiche-reason-chip-${c.id}`}
                onClick={() => setRescheduleReason((prev) => (prev === c.label ? '' : c.label))}
                className={cn('rounded-full px-2 py-0.5 text-[11px]', rescheduleReason === c.label ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70')}>
                {c.label}
              </button>
            ))}
          </div>
          <input type="text" value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)}
            placeholder="Pourquoi ce changement ?" data-testid="planning-fiche-reschedule-reason"
            className="w-full rounded border px-2 py-1 text-xs" />
        </div>
      )}

      {error && <p className="mb-2 text-[11px] text-red-600" data-testid="planning-fiche-error">{error}</p>}

      <div className="flex items-center justify-between gap-2">
        {canManage && mode === 'edit' && props.onCancelEvent ? (
          <button type="button" onClick={() => setShowCancel((v) => !v)} data-testid="planning-fiche-cancel-toggle"
            className="text-[11px] text-red-600 hover:underline">Annuler l’événement</button>
        ) : <span />}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded px-2 py-1 text-xs text-muted-foreground">Fermer</button>
          <button type="button" onClick={() => void submit()} disabled={submitting || !canManage}
            title={canManage ? undefined : PERMISSION_DENIED_HINT[PERMISSIONS.PLANNING_MANAGE]}
            data-testid="planning-fiche-submit"
            className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50">
            {mode === 'edit' ? 'Replanifier' : 'Planifier'}
          </button>
        </div>
      </div>

      {showCancel && mode === 'edit' && (
        <div className="mt-2 border-t pt-2">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif d’annulation (≥ 5 car.)"
            data-testid="planning-fiche-cancel-reason" className="mb-2 w-full rounded border px-2 py-1 text-xs" />
          <button type="button" onClick={() => void cancelEvent()} disabled={submitting} data-testid="planning-fiche-cancel-confirm"
            className="w-full rounded bg-red-600 px-3 py-1 text-xs text-white disabled:opacity-50">Confirmer l’annulation</button>
        </div>
      )}
    </div>
  )
}
